import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api-client';
import type { InvoiceRow } from '../types';
import { blOrderInvoicesKey } from '../api/use-bl-order-invoices';
import { allocatedDownpaymentKey } from '../api/use-allocated-downpayment';
import { appliedProvisionalKey } from '../api/use-applied-provisional';
import type { InvoiceDirection } from '../api/use-applied-provisional';
import { coreValuesKey } from '../api/use-core-values';
import { saveInvoiceWithPdf } from './save-invoice-with-pdf';
import { useInvoiceCreatePdf } from './invoice-create-pdf-context';
import { buildFinalSettlementSavePayload, buildProvisionalSavePayload } from './trade-invoice-wire';
import type { ProvisionalSubmitValues } from './provisional-invoice-schema';
import type { FinalSettlementSubmitValues } from './final-settlement-invoice-schema';

function extractApiErrorMessage(err: Error, fallback: string): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body !== null) {
    const body = err.body as Record<string, unknown>;
    const msg = body['message'];
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg) && msg.length > 0 && typeof msg[0] === 'string') return msg[0];
  }
  return fallback;
}

type Kind = 'Provisional' | 'Final Settlement';

interface UseSaveTradeMutationArgs<TValues> {
  readonly kind: Kind;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly onSaved: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly buildPayload: (values: TValues) => Record<string, unknown>;
}

function useSaveTradeMutation<TValues>(args: UseSaveTradeMutationArgs<TValues>) {
  const { kind, side, orderId, blOrderId, existingInvoice, onSaved, onOpenChange, buildPayload } =
    args;
  const qc = useQueryClient();
  const { file: pdfFile } = useInvoiceCreatePdf();
  const direction: InvoiceDirection = side === 'buy' ? 'Purchase' : 'Sale';
  return useMutation<unknown, Error, TValues>({
    mutationFn: (values) => saveInvoiceWithPdf(existingInvoice, buildPayload(values), pdfFile),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(blOrderId) });
      void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(blOrderId) });
      void qc.invalidateQueries({ queryKey: appliedProvisionalKey(blOrderId, direction) });
      // The save may have appended to the value log — refresh the grid live.
      void qc.invalidateQueries({ queryKey: coreValuesKey(blOrderId) });
      void qc.invalidateQueries({ queryKey: ['order-advance-invoices', orderId] });
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
      toast.success(
        existingInvoice !== null ? `${kind} invoice updated.` : `${kind} invoice created.`,
      );
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(extractApiErrorMessage(err, `Failed to save ${kind} invoice.`));
    },
  });
}

export function useSaveProvisionalMutation(args: {
  side: 'buy' | 'sell';
  orderId: string;
  blOrderId: string;
  existingInvoice: InvoiceRow | null;
  onSaved: () => void;
  onOpenChange: (open: boolean) => void;
  aiExtractionId?: string;
}) {
  return useSaveTradeMutation<ProvisionalSubmitValues>({
    ...args,
    kind: 'Provisional',
    buildPayload: (values) =>
      buildProvisionalSavePayload(values, {
        side: args.side,
        orderId: args.orderId,
        blOrderId: args.blOrderId,
        existingInvoice: args.existingInvoice,
        ...(args.aiExtractionId !== undefined ? { aiExtractionId: args.aiExtractionId } : {}),
      }),
  });
}

export function useSaveFinalSettlementMutation(args: {
  side: 'buy' | 'sell';
  orderId: string;
  blOrderId: string;
  existingInvoice: InvoiceRow | null;
  onSaved: () => void;
  onOpenChange: (open: boolean) => void;
  aiExtractionId?: string;
}) {
  return useSaveTradeMutation<FinalSettlementSubmitValues>({
    ...args,
    kind: 'Final Settlement',
    buildPayload: (values) =>
      buildFinalSettlementSavePayload(values, {
        side: args.side,
        orderId: args.orderId,
        blOrderId: args.blOrderId,
        existingInvoice: args.existingInvoice,
        ...(args.aiExtractionId !== undefined ? { aiExtractionId: args.aiExtractionId } : {}),
      }),
  });
}
